import { Link } from 'react-router-dom'
import type { DeliveryScheduleItemOut } from '../api/types'
import { chequeUncleared } from '../lib/deliveries'
import { count, formatDate, fullName, humanise } from '../lib/format'

/**
 * Delivery items with a checkbox each, for the inbox and the day plan. The
 * flags column carries everything a coordinator weighs before sending a truck:
 * cheques, re-attempts, stairs, pets and the customer's preferred date.
 */
export function DispatchTable({
  items,
  selected,
  onToggle,
}: {
  items: DeliveryScheduleItemOut[]
  selected: Set<string>
  onToggle: (ids: string[], on: boolean) => void
}) {
  const ids = items.map((item) => item.id)
  const allOn = ids.length > 0 && ids.every((id) => selected.has(id))

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ width: 32 }}>
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allOn}
                onChange={() => onToggle(ids, !allOn)}
              />
            </th>
            <th>Invoice</th>
            <th>Customer</th>
            <th>Item</th>
            <th className="num">Qty</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className={selected.has(item.id) ? 'selected' : undefined}
              onClick={(event) => {
                // Whole-row toggling, except on the links inside it.
                if ((event.target as HTMLElement).closest('a, input')) return
                onToggle([item.id], !selected.has(item.id))
              }}
            >
              <td>
                <input
                  type="checkbox"
                  aria-label={`Select ${item.item_name ?? 'item'} on ${item.invoice_reference ?? 'invoice'}`}
                  checked={selected.has(item.id)}
                  onChange={() => onToggle([item.id], !selected.has(item.id))}
                />
              </td>
              <td>
                <Link to={`/invoices/${item.invoice_id}`}>{item.invoice_reference ?? '—'}</Link>
                <div className="cell-sub">{formatDate(item.created_at)}</div>
              </td>
              <td>
                {fullName(item.customer_first_name, item.customer_last_name)}
                {item.delivery_address ? <div className="cell-sub">{item.delivery_address}</div> : null}
              </td>
              <td>
                {item.item_name ?? '—'}
                {item.item_model ? <div className="cell-sub">{item.item_model}</div> : null}
              </td>
              <td className="num">{count(item.qty)}</td>
              <td>
                <Flags item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Flags({ item }: { item: DeliveryScheduleItemOut }) {
  const chips = []
  if (chequeUncleared(item)) chips.push(<span key="cheque" className="chip orange">Cheque not cleared</span>)
  if (item.previously_attempted) {
    const last = item.previous_failure_details.at(-1)
    chips.push(
      <span key="retry" className="chip red" title={last?.reason_notes ?? undefined}>
        Re-attempt{last?.reason ? ` · ${humanise(last.reason)}` : ''}
      </span>,
    )
  }
  if (item.delivery_has_stairs) chips.push(<span key="stairs" className="chip grey">Stairs</span>)
  if (item.delivery_has_pets) chips.push(<span key="pets" className="chip grey">Pets</span>)
  if (item.preferred_delivery_date)
    chips.push(
      <span key="pref" className="chip blue">
        Prefers {item.preferred_delivery_date}
      </span>,
    )

  return (
    <>
      {chips.length ? <div className="row" style={{ gap: 4 }}>{chips}</div> : null}
      {item.delivery_notes ? <div className="cell-sub">{item.delivery_notes}</div> : null}
      {item.delivery_alt_contact_name || item.delivery_alt_contact_phone ? (
        <div className="cell-sub">
          Alt contact: {[item.delivery_alt_contact_name, item.delivery_alt_contact_phone].filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {!chips.length && !item.delivery_notes ? <span className="cell-sub">—</span> : null}
    </>
  )
}
